import React, { useState } from 'react';

import delv from 'dlv';
import { useIntl } from 'react-intl';
import styled, { css } from 'styled-components';

import { IPickerAspect, IPickerAspectUiPattern, IPickerPromotion } from '@rbi-ctg/menu';
import { OrderedHotDrinkSizeArray } from 'components/icons/size-hot-drink';
import PickerAspectOption from 'components/picker-aspect-option';
import InitialPickerAspectOption from 'components/picker-aspect-option/initial';
import TertiaryButton from 'components/tertiary-button';
import { useFormatJoules } from 'hooks/use-format-joules';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { PickerAspectUiPattern, isUiPatternRadio } from 'state/menu/types';
import { formatCaloriesToJoulesFromString } from 'utils/menu/calories';

import { MIN_PICKER_ASPECT_OPTIONS } from './constants';

interface IProps extends IPickerAspectUiPattern {
  handleSelection(id: string, identifier: string): void;
  aspect: IPickerAspect;
  aspectPromotionMap?: {
    [id: string]: IPickerPromotion;
  };
  initialStepUI: boolean;
  selectedPickerIdentifiers: {
    [id: string]: string;
  };
  calorieMap: {
    [identifier: string]: string;
  };
  pricePickerAspect: (pickerAspectId: string, pickerAspectOptionId: string) => string;
  isInStaticMenu: boolean;
}

interface IForm extends IPickerAspectUiPattern {
  children: React.ReactNode[];
}

const Form = styled.form<IForm>`
  ${p => {
    return (
      isUiPatternRadio(p) &&
      css`
        display: flex;
        flex-wrap: wrap;
        justify-content: center;
        width: 100%;

        > * {
          width: ${Math.max(100 / React.Children.count(p.children), 25)}%;
        }
      `
    );
  }}
`;

// COMMENT 1.
// When changing this file to ts, i found that the pickerAspectOptions have been
// monkey patched to include a `disabled` property.
// With type systems, we should avoid changing object shape.
// It'll take more work than this PR deserves to fix, so I added ts-ignores
// But we will need to remove the object patching on the pickerAspectOptions.

const PickerDetail: React.FC<IProps> = props => {
  const { formatMessage } = useIntl();
  const formatJoules = useFormatJoules();
  const [showAllOpts, setShowAllOpts] = useState(false);
  const enableEUNutritionDisplay = useFlag(LaunchDarklyFlag.ENABLE_EU_NUTRITION_DISPLAY);
  const optionsAndModifiersCaloriesIsEnabled = useFlag(
    LaunchDarklyFlag.ENABLE_CALORIES_FOR_OPTIONS_AND_MODIFIERS
  );

  const aspectId = props.aspect._id;

  const handleSelection = (identifier: string) => {
    props.handleSelection(aspectId, identifier);
  };

  // By default all `showInStaticMenu` is undefined. To hide in the UI it has to be explicitely false. This is the one minor difference from the other `showInStaticMenu` we have this is almost the opposite.
  // On all the others having that boolean undefined will hide it in the UI. This is to avoid having to update all aspects to turn this option on.
  const filteredAspectOptions = props.isInStaticMenu
    ? props.aspect.pickerAspectOptions.filter(option => option?.showInStaticMenu !== false)
    : props.aspect.pickerAspectOptions;

  // Filter out the picker options marked as disabled
  const enabledOptions = filteredAspectOptions.filter(option => !option.disabled);

  const visibleOptions =
    props.initialStepUI && !showAllOpts
      ? enabledOptions.slice(0, MIN_PICKER_ASPECT_OPTIONS)
      : enabledOptions;

  const selectedPickerIdentifier = props.selectedPickerIdentifiers[aspectId];
  const uiPattern = delv(props, 'aspect.uiPattern', PickerAspectUiPattern.SELECTOR);

  return (
    <Form uiPattern={uiPattern}>
      {(visibleOptions || []).map((option, i) => {
        const { identifier } = option;
        const currentCalories = props.calorieMap[identifier];

        const caloriesInJoules = formatCaloriesToJoulesFromString(
          currentCalories || '',
          formatJoules
        );

        const euFormattedNutritionText = formatMessage(
          { id: 'euNutritionValues' },
          { calories: currentCalories, joules: caloriesInJoules }
        );

        const globalizedCalorieFormat = currentCalories ? `${euFormattedNutritionText}` : '';

        let calories: string | undefined = undefined;
        if (optionsAndModifiersCaloriesIsEnabled) {
          calories = enableEUNutritionDisplay ? globalizedCalorieFormat : currentCalories;
        }
        const promotion = props?.aspectPromotionMap?.[`${aspectId}-${identifier}`];

        return props.initialStepUI ? (
          <InitialPickerAspectOption
            key={identifier}
            option={option}
            uiPattern={uiPattern}
            handleSelection={handleSelection}
            icon={OrderedHotDrinkSizeArray[i]}
            inputName={props.aspect.name?.locale}
            // @ts-ignore SEE COMMENT 1 ABOVE
            disabled={option.disabled}
            selected={selectedPickerIdentifier === identifier}
            calories={calories}
            description={delv(option.description, 'locale')}
            price={props.pricePickerAspect(props.aspect._id, identifier)}
            aspectPromotion={promotion}
          />
        ) : (
          <PickerAspectOption
            key={identifier}
            option={option}
            uiPattern={uiPattern}
            handleSelection={handleSelection}
            icon={OrderedHotDrinkSizeArray[i]}
            inputName={props.aspect.name?.locale}
            selected={selectedPickerIdentifier === identifier}
            // @ts-ignore SEE COMMENT 1 ABOVE
            disabled={option.disabled}
            calories={calories}
            description={delv(option.description, 'locale')}
            price={props.pricePickerAspect(props.aspect._id, identifier)}
            aspectPromotion={promotion}
          />
        );
      })}
      {props.initialStepUI &&
        !showAllOpts &&
        filteredAspectOptions.length > MIN_PICKER_ASPECT_OPTIONS && (
          <TertiaryButton data-testid="picker-more-options" onClick={() => setShowAllOpts(s => !s)}>
            {filteredAspectOptions[MIN_PICKER_ASPECT_OPTIONS].name?.locale} and more options
          </TertiaryButton>
        )}
    </Form>
  );
};

PickerDetail.defaultProps = {
  pricePickerAspect: () => '',
};

export default PickerDetail;
